/**
 * Notification-kind presentation map.
 *
 * The feed API returns raw `notification_deliveries` rows whose only semantic
 * field is `notification_kind` (a code) — there is no stored human summary. We
 * therefore DERIVE an i18n label key + icon from the kind code (honest: we map a
 * code to copy, we do not fabricate row content). Unknown kinds fall back to the
 * humanized kind string so nothing is hidden.
 *
 * Labels resolve through i18next (`t(labelKey)`); the cs/en values live in the
 * `ntf.kind.*` / `ntf.filter.*` bundles. Filter chips set the `kind` query param
 * to the exact backend kind value (the controller filters by equality).
 * Spec §NTF Page 3.
 */
import type * as React from 'react';
import {
  CheckboxLine,
  MailSendLine,
  MailboxLine,
  WarningLine,
  NotificationLine,
} from '@teivor/shared-ui';

export type KindIcon = React.ComponentType<{ size?: number | string; color?: string }>;

interface KindMeta {
  labelKey: string;
  Icon: KindIcon;
}

const KIND_META: Record<string, KindMeta> = {
  draft_approval: { labelKey: 'ntf.kind.draft_approval', Icon: CheckboxLine },
  send_approval: { labelKey: 'ntf.kind.send_approval', Icon: MailSendLine },
  handoff_first_touch: { labelKey: 'ntf.kind.handoff_first_touch', Icon: MailSendLine },
  auth_broken_conversation: { labelKey: 'ntf.kind.auth_broken_conversation', Icon: MailboxLine },
  ghost_auto_close: { labelKey: 'ntf.kind.ghost_auto_close', Icon: WarningLine },
};

/** i18n key for a known kind's label, or null for unknown kinds (caller humanizes). */
export function kindLabelKey(kind: string): string | null {
  return KIND_META[kind]?.labelKey ?? null;
}

/** Fallback display for unknown kinds: humanized code (never hide the row). */
export function humanizeKind(kind: string): string {
  return kind.replace(/_/g, ' ');
}

export function kindIcon(kind: string): KindIcon {
  return KIND_META[kind]?.Icon ?? NotificationLine;
}

/** Filter chips → exact backend `kind` value (undefined = no filter). */
export interface KindFilter {
  id: string;
  labelKey: string;
  /** undefined → "All" (no kind param). */
  kind?: string;
}

export const KIND_FILTERS: KindFilter[] = [
  { id: 'all', labelKey: 'ntf.filter.all' },
  { id: 'approvals', labelKey: 'ntf.filter.approvals', kind: 'draft_approval' },
  { id: 'sendApprovals', labelKey: 'ntf.filter.send_approvals', kind: 'send_approval' },
  { id: 'deliverability', labelKey: 'ntf.filter.deliverability', kind: 'auth_broken_conversation' },
  { id: 'handoffs', labelKey: 'ntf.filter.handoffs', kind: 'handoff_first_touch' },
];
